import { createContext, useState, useContext, useMemo, useEffect } from 'react';

import { useIsHydrated } from '../../hooks';
import { useGetMyUserAgreementQuery } from '../../store';
import { UserContextValue, UserProviderProps, User } from './UserContext.types';
import { getAvatarSrc, isAuthorizedByBlackbox } from './UserContext.helpers';

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider = ({ children }: UserProviderProps) => {
    const isHydrated = useIsHydrated();
    // При SSR считаем пользователя авторизованным с данными по умолчанию
    const [user, setUser] = useState<User>({
        isAuth: true,
        login: '',
        firstName: '',
        lastName: '',
        avatarSrc: getAvatarSrc(),
    });

    const { data: userAgreement } = useGetMyUserAgreementQuery(undefined, { skip: !user.isAuth || !isHydrated });
    const isPublicProfile = Boolean(userAgreement?.isPublic);

    // При гидратации получаем реальные данные пользователя из window.__APP_DATA__
    useEffect(() => {
        if (window.__APP_DATA__) {
            const { user } = window.__APP_DATA__;

            if (!isAuthorizedByBlackbox(user)) {
                setUser({ isAuth: false });

                return;
            }

            setUser({
                isAuth: true,
                login: user.login,
                firstName: user.firstName,
                lastName: user.lastName,
                avatarSrc: getAvatarSrc(user.avatar),
                uid: user.uid,
                publicId: user.publicId,
                isPublicProfile: isPublicProfile,
            });
        }
    }, [isPublicProfile]);

    const value: UserContextValue = useMemo(() => ({ user }), [user]);

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextValue => {
    const context = useContext(UserContext);

    if (context === undefined) {
        throw new Error('useUserContext must be used within a UserProvider');
    }

    return context;
};
